/*
 * Copyright Evollis 2021
 * All Rights Reserved.
 *
 * All information contained herein is, and remains the property of Evollis.
 * The intellectual and technical concepts contained herein are
 * proprietary to Evollis and are protected by trade secret and copyright law.
 * Dissemination of this information or reproduction of this material
 * is STRICTLY FORBIDDEN unless prior written permission is obtained
 * from Evollis.
 */
import { getCountryFromLanguage } from "../../../core/service/BusinessLogicService"

/* Utils */
import { loc } from "../../../core/utils"
import AutoCompleteSelect from "../../../form/AutoCompleteSelect"
import Date from "../../../form/Date"
import RadiosField from "../../../form/RadiosField"
import Select from "../../../form/Select"

/* Forms controls & validation */
import TextField from "../../../form/TextField"
import {
  DATE_FORMAT,
  EXTRA_UNION_EUROPE,
  IDENTITY_DOCUMENT_TYPE_PATENTE,
  ITALIANA,
  RICHIESTA_RINNOVO
} from "../constants/ClientPageConstants"
import { getTemporaryDocuments } from "../service/ClientPageServices"
import Bloc from "./Bloc"
import React from "react"
import { Col, Row } from "react-bootstrap"

export default function IdentityDocumentsBloc(props) {
  const { tunnelData, values, country, blocFields, completedFields } = props
  const { clientPageConfiguration } = tunnelData
  const { identityDocuments } = clientPageConfiguration
  const orderLanguageTag = tunnelData?.data?.order?.orderLanguageTag
  const countryCodeFromLanguage = getCountryFromLanguage(orderLanguageTag)
  const languageTag = orderLanguageTag || ""

  return (
    <Bloc
      hide={!identityDocuments}
      name="My identity documents"
      completedFields={completedFields}
      totalFieldsNumber={blocFields.length}
    >
      <div className="hard-shadow-white-block">
        <Row>
          <Col lg={12}>
            <Select
              hidden={!identityDocuments?.includes("citizenshipSimplified")}
              name="identityDocuments.citizenshipSimplified"
              label={loc`CitizenshipIT`}
              placeholder={loc`Select...`}
              url={
                tunnelData.apiUrl + "middle-business-service/api/v2/repository/citizen-ships/" + countryCodeFromLanguage
              }
              token={tunnelData.access_token}
              disabled={
                tunnelData.data.order.simplifiedJourneyEnabled &&
                tunnelData?.simplifiedJourneyInputConfiguration?.identityDocuments?.citizenshipSimplified?.editable ===
                  false
              }
            />
          </Col>
          {values.identityDocuments.citizenshipSimplified?.value !== ITALIANA &&
            values.identityDocuments.citizenshipSimplified &&
            identityDocuments?.includes("citizenshipExtended") && (
              <Col lg={12}>
                <Select
                  name="identityDocuments.citizenshipExtended"
                  label={loc`citizenshipExtended`}
                  placeholder={loc`Select...`}
                  url={
                    tunnelData.apiUrl +
                    "middle-business-service/api/v2/repository/countries/codes/" +
                    countryCodeFromLanguage +
                    "/" +
                    values.identityDocuments.citizenshipSimplified?.value +
                    `?languageTag=${encodeURIComponent(languageTag)}`
                  }
                  token={tunnelData.access_token}
                  disabled={
                    tunnelData.data.order.simplifiedJourneyEnabled &&
                    tunnelData?.simplifiedJourneyInputConfiguration?.identityDocuments?.citizenshipExtended
                      ?.editable === false
                  }
                />
              </Col>
            )}
          <Col lg={12}>
            <Select
              hidden={!identityDocuments?.includes("identityDocumentType")}
              name="identityDocuments.identityDocumentType"
              label={loc`Identity document type`}
              placeholder={loc`Select...`}
              url={
                tunnelData.apiUrl +
                "middle-business-service/api/v2/repository/identity-document/list/" +
                countryCodeFromLanguage
              }
              token={tunnelData.access_token}
              disabled={
                tunnelData.data.order.simplifiedJourneyEnabled &&
                tunnelData?.simplifiedJourneyInputConfiguration?.identityDocuments?.identityDocumentType?.editable ===
                  false
              }
            />
          </Col>
          <Col lg={12}>
            <TextField
              hidden={!identityDocuments?.includes("identityDocumentsNumber")}
              name="identityDocuments.identityDocumentsNumber"
              label={loc`Identity document number`}
              trimOnBlur={true}
              disabled={
                tunnelData.data.order.simplifiedJourneyEnabled &&
                tunnelData?.simplifiedJourneyInputConfiguration?.identityDocuments?.identityDocumentsNumber
                  ?.editable === false
              }
            />
          </Col>
          <Col lg={6}>
            {countryCodeFromLanguage !== "IT" && (
              <TextField
                hidden={!identityDocuments?.includes("documentPlaceOfIssue")}
                name="identityDocuments.documentPlaceOfIssue"
                label={loc`Place of issue`}
                trimOnBlur={true}
                disabled={
                  tunnelData.data.order.simplifiedJourneyEnabled &&
                  tunnelData?.simplifiedJourneyInputConfiguration?.identityDocuments?.documentPlaceOfIssue?.editable ===
                    false
                }
              />
            )}
            {countryCodeFromLanguage === "IT" &&
              !IDENTITY_DOCUMENT_TYPE_PATENTE.includes(values.identityDocuments.identityDocumentType?.value) && (
                <AutoCompleteSelect
                  hidden={!identityDocuments?.includes("documentPlaceOfIssue")}
                  name="identityDocuments.documentPlaceOfIssue"
                  label={loc`Place of issue`}
                  trimOnBlur={true}
                  url={
                    tunnelData.apiUrl +
                    `middle-business-service/api/v2/repository/cities/IT?city=INPUT_VALUE&limit=10&languageTag=${encodeURIComponent(
                      languageTag
                    )}`
                  }
                  token={tunnelData.access_token}
                  disabled={
                    tunnelData.data.order.simplifiedJourneyEnabled &&
                    tunnelData?.simplifiedJourneyInputConfiguration?.identityDocuments?.documentPlaceOfIssue
                      ?.editable === false
                  }
                  defaultValue={tunnelData.data.order.customer.identityDocumentCreationLocation}
                />
              )}
            {countryCodeFromLanguage === "IT" &&
              IDENTITY_DOCUMENT_TYPE_PATENTE.includes(values.identityDocuments.identityDocumentType?.value) && (
                <AutoCompleteSelect
                  hidden={!identityDocuments?.includes("documentPlaceOfIssue")}
                  name="identityDocuments.documentPlaceOfIssue"
                  label={loc`Place of issue`}
                  trimOnBlur={true}
                  url={
                    tunnelData.apiUrl +
                    "middle-business-service/api/v2/repository/provinces/IT?province=INPUT_VALUE&limit=10"
                  }
                  token={tunnelData.access_token}
                  disabled={
                    tunnelData.data.order.simplifiedJourneyEnabled &&
                    tunnelData?.simplifiedJourneyInputConfiguration?.identityDocuments?.documentPlaceOfIssue
                      ?.editable === false
                  }
                />
              )}
          </Col>
          <Col lg={6}>
            <Date
              hidden={!identityDocuments?.includes("documentDateOfIssue")}
              name="identityDocuments.documentDateOfIssue"
              label={loc`Date of issue`}
              format={DATE_FORMAT.identityDocuments.documentDateOfIssue[country]}
              disabled={
                tunnelData.data.order.simplifiedJourneyEnabled &&
                tunnelData?.simplifiedJourneyInputConfiguration?.identityDocuments?.documentDateOfIssue?.editable ===
                  false
              }
            />
          </Col>
          <Col lg={6}>
            <TextField
              hidden={
                !identityDocuments?.includes("identityDocumentsDriverLicenseNumber") ||
                !IDENTITY_DOCUMENT_TYPE_PATENTE.includes(values.identityDocuments.identityDocumentType?.value)
              }
              name="identityDocuments.identityDocumentsDriverLicenseNumber"
              label={loc`Number of driving license`}
              trimOnBlur={true}
              disabled={
                tunnelData.data.order.simplifiedJourneyEnabled &&
                tunnelData?.simplifiedJourneyInputConfiguration?.identityDocuments?.identityDocumentsDriverLicenseNumber
                  ?.editable === false
              }
            />
          </Col>
          {values.identityDocuments.citizenshipSimplified?.value === EXTRA_UNION_EUROPE && (
            <Col lg={12}>
              <Select
                hidden={!identityDocuments?.includes("residencePermitType")}
                name="identityDocuments.residencePermitType"
                label={loc`Permit, residence permit`}
                placeholder={loc`Select...`}
                url={
                  tunnelData.apiUrl +
                  "middle-business-service/api/v2/repository/identity-document/list/" +
                  countryCodeFromLanguage +
                  "?insideCee= + false"
                }
                token={tunnelData.access_token}
                disabled={
                  tunnelData.data.order.simplifiedJourneyEnabled &&
                  tunnelData?.simplifiedJourneyInputConfiguration?.identityDocuments?.residencePermitType?.editable ===
                    false
                }
              />
            </Col>
          )}
          <Col lg={12}>
            {values.identityDocuments.citizenshipSimplified?.value === EXTRA_UNION_EUROPE &&
              identityDocuments?.includes("temporaryDocument") && (
                <RadiosField
                  name="identityDocuments.temporaryDocument"
                  checked={values.identityDocuments.temporaryDocument}
                  items={getTemporaryDocuments(tunnelData)}
                  label={loc`Temporary document`}
                  disabled={
                    tunnelData.data.order.simplifiedJourneyEnabled &&
                    tunnelData?.simplifiedJourneyInputConfiguration?.identityDocuments?.temporaryDocument?.editable ===
                      false
                  }
                />
              )}
          </Col>
          {values.identityDocuments.citizenshipSimplified?.value === EXTRA_UNION_EUROPE &&
            (values.identityDocuments.temporaryDocument === RICHIESTA_RINNOVO ||
              values.identityDocuments.temporaryDocument === null) &&
            identityDocuments?.includes("residencePermitExpiryDate") && (
              <Col lg={6}>
                <Date
                  hidden={!identityDocuments?.includes("residencePermitExpiryDate")}
                  name="identityDocuments.residencePermitExpiryDate"
                  label={loc`Expiry residence permit date`}
                  format={DATE_FORMAT.identityDocuments.residencePermitExpiryDate[country]}
                  disabled={
                    tunnelData.data.order.simplifiedJourneyEnabled &&
                    tunnelData?.simplifiedJourneyInputConfiguration?.identityDocuments?.residencePermitExpiryDate
                      ?.editable === false
                  }
                />
              </Col>
            )}
        </Row>
      </div>
    </Bloc>
  )
}
